import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";

const EMPTY = {
    scope: "merchant", shop_id: null, enabled: true,
    imap_host: "", imap_port: 993, imap_use_ssl: true, imap_user: "", imap_password: "",
    smtp_host: "", smtp_port: 587, smtp_use_tls: true, smtp_user: "", smtp_password: "",
    from_email: "", from_name: "",
};

export default function EmailSettings() {
    const { t } = useI18n();
    const [shops, setShops] = useState([]);
    const [selectedScope, setSelectedScope] = useState("merchant");
    const [selectedShopId, setSelectedShopId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [existing, setExisting] = useState(null);
    const [allSettings, setAllSettings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = async () => {
        const [{ data: sh }, { data: es }] = await Promise.all([api.get("/shops"), api.get("/email-settings")]);
        setShops(sh);
        setAllSettings(es);
    };
    useEffect(() => { load(); }, []);

    // Derive the matching saved config for current scope/shop selection.
    const existingMatch = allSettings.find((s) =>
        (selectedScope === "merchant" && s.scope === "merchant") ||
        (selectedScope === "shop" && s.scope === "shop" && s.shop_id === selectedShopId)
    ) || null;

    // Only reset the form when the *identity* of the saved config changes
    // (scope/shop switch or a save/delete completes). Avoids wiping user input
    // mid-edit just because allSettings was re-fetched.
    useEffect(() => {
        if (existingMatch) {
            setExisting(existingMatch);
            setForm({ ...EMPTY, ...existingMatch, imap_password: "", smtp_password: "", shop_id: selectedShopId });
        } else {
            setExisting(null);
            setForm({ ...EMPTY, scope: selectedScope, shop_id: selectedShopId });
        }
        setTestResult(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingMatch?.id, selectedScope, selectedShopId]);

    const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            const payload = { ...form };
            // Don't send empty password (keeps existing)
            if (!payload.imap_password) delete payload.imap_password;
            if (!payload.smtp_password) delete payload.smtp_password;
            if (payload.scope === "merchant") payload.shop_id = null;
            await api.put("/email-settings", payload);
            // Clear password fields immediately so the user sees the masked state
            setForm((p) => ({ ...p, imap_password: "", smtp_password: "" }));
            toast.success(t("email_settings_saved"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("toast_save_failed"));
        } finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const payload = { ...form };
            if (!payload.imap_password) delete payload.imap_password;
            if (!payload.smtp_password) delete payload.smtp_password;
            if (payload.scope === "merchant") payload.shop_id = null;
            const { data } = await api.post("/email-settings/test", payload);
            setTestResult(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("email_test_failed"));
        } finally { setTesting(false); }
    };

    const remove = async () => {
        try {
            await api.delete("/email-settings", { params: { scope: selectedScope, shop_id: selectedShopId || undefined } });
            toast.success(t("email_settings_removed"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("toast_delete_failed"));
        }
    };

    return (
        <div className="max-w-3xl" data-testid="email-settings">
            <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-md bg-[#0b905d] flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h2 className="font-display text-2xl font-bold text-[#00153f]">{t("email_settings_title")}</h2>
                    <p className="text-sm text-gray-600">{t("email_settings_sub")}</p>
                </div>
            </div>

            {/* Scope selector */}
            <div className="border border-gray-200 bg-white rounded-md p-5 mb-5">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("email_scope")}</Label>
                        <Select value={selectedScope} onValueChange={(v) => { setSelectedScope(v); setSelectedShopId(null); }}>
                            <SelectTrigger className="mt-2" data-testid="email-scope-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="merchant">{t("email_scope_merchant")}</SelectItem>
                                <SelectItem value="shop">{t("email_scope_shop")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {selectedScope === "shop" && (
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("nav_shops")}</Label>
                            <Select value={selectedShopId || ""} onValueChange={setSelectedShopId}>
                                <SelectTrigger className="mt-2" data-testid="email-shop-select"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    {shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
            </div>

            {(selectedScope === "merchant" || selectedShopId) && (
                <form onSubmit={save} className="space-y-5">
                    {/* From */}
                    <div className="border border-gray-200 bg-white rounded-md p-5">
                        <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-4">{t("email_sender")}</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>{t("email_from_email")} *</Label>
                                <Input type="email" required value={form.from_email} onChange={(e) => setField("from_email", e.target.value)} data-testid="email-from-email" className="mt-2" />
                            </div>
                            <div>
                                <Label>{t("email_from_name")}</Label>
                                <Input value={form.from_name || ""} onChange={(e) => setField("from_name", e.target.value)} data-testid="email-from-name" className="mt-2" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mt-4">
                            <Switch checked={form.enabled} onCheckedChange={(v) => setField("enabled", v)} data-testid="email-enabled" />
                            <Label>{t("email_enabled")}</Label>
                        </div>
                    </div>

                    {/* IMAP */}
                    <div className="border border-gray-200 bg-white rounded-md p-5">
                        <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-4">IMAP ({t("email_inbound")})</div>
                        <div className="grid sm:grid-cols-3 gap-4">
                            <div className="sm:col-span-2">
                                <Label>{t("email_host")} *</Label>
                                <Input required value={form.imap_host} onChange={(e) => setField("imap_host", e.target.value)} placeholder="imap.example.com" data-testid="imap-host" className="mt-2" />
                            </div>
                            <div>
                                <Label>{t("email_port")}</Label>
                                <Input type="number" value={form.imap_port} onChange={(e) => setField("imap_port", parseInt(e.target.value || "993", 10))} data-testid="imap-port" className="mt-2" />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4 mt-4">
                            <div>
                                <Label>{t("email_user")} *</Label>
                                <Input required value={form.imap_user} onChange={(e) => setField("imap_user", e.target.value)} data-testid="imap-user" className="mt-2" />
                            </div>
                            <div>
                                <Label>{t("email_password")} {existing ? `(${t("email_keep_existing")})` : "*"}</Label>
                                <Input type="password" autoComplete="new-password" placeholder={existing ? "••••••••" : ""} value={form.imap_password} onChange={(e) => setField("imap_password", e.target.value)} data-testid="imap-password" className="mt-2" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mt-4">
                            <Switch checked={form.imap_use_ssl} onCheckedChange={(v) => setField("imap_use_ssl", v)} data-testid="imap-ssl" />
                            <Label>{t("email_use_ssl")}</Label>
                        </div>
                    </div>

                    {/* SMTP */}
                    <div className="border border-gray-200 bg-white rounded-md p-5">
                        <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-4">SMTP ({t("email_outbound")})</div>
                        <div className="grid sm:grid-cols-3 gap-4">
                            <div className="sm:col-span-2">
                                <Label>{t("email_host")} *</Label>
                                <Input required value={form.smtp_host} onChange={(e) => setField("smtp_host", e.target.value)} placeholder="smtp.example.com" data-testid="smtp-host" className="mt-2" />
                            </div>
                            <div>
                                <Label>{t("email_port")}</Label>
                                <Input type="number" value={form.smtp_port} onChange={(e) => setField("smtp_port", parseInt(e.target.value || "587", 10))} data-testid="smtp-port" className="mt-2" />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4 mt-4">
                            <div>
                                <Label>{t("email_user")} *</Label>
                                <Input required value={form.smtp_user} onChange={(e) => setField("smtp_user", e.target.value)} data-testid="smtp-user" className="mt-2" />
                            </div>
                            <div>
                                <Label>{t("email_password")} {existing ? `(${t("email_keep_existing")})` : "*"}</Label>
                                <Input type="password" autoComplete="new-password" placeholder={existing ? "••••••••" : ""} value={form.smtp_password} onChange={(e) => setField("smtp_password", e.target.value)} data-testid="smtp-password" className="mt-2" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mt-4">
                            <Switch checked={form.smtp_use_tls} onCheckedChange={(v) => setField("smtp_use_tls", v)} data-testid="smtp-tls" />
                            <Label>{t("email_use_tls")}</Label>
                        </div>
                    </div>

                    {/* Test result */}
                    {testResult && (
                        <div className="border border-gray-200 bg-white rounded-md p-4 space-y-2" data-testid="email-test-result">
                            <ResultLine ok={testResult.imap === "ok"} label="IMAP" err={testResult.imap_error} />
                            <ResultLine ok={testResult.smtp === "ok"} label="SMTP" err={testResult.smtp_error} />
                        </div>
                    )}

                    {/* Status */}
                    {existing?.last_poll_at && (
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                            <RefreshCw className="w-3.5 h-3.5" />
                            {t("email_last_poll")}: {new Date(existing.last_poll_at).toLocaleString()}
                            {existing.last_poll_error && <span className="text-red-600">· {existing.last_poll_error}</span>}
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2 justify-between">
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={test} disabled={testing} data-testid="email-test-btn">
                                {testing ? "..." : t("email_test")}
                            </Button>
                            {existing && (
                                <Button type="button" variant="outline" onClick={remove} className="text-red-600 border-red-200" data-testid="email-delete-btn">
                                    {t("delete")}
                                </Button>
                            )}
                        </div>
                        <Button type="submit" disabled={saving} className="bg-[#0b905d] hover:bg-[#067a4d] text-white" data-testid="email-save-btn">
                            {saving ? "..." : t("save")}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}

function ResultLine({ ok, label, err }) {
    return (
        <div className="flex items-center gap-2 text-sm">
            {ok ? <CheckCircle2 className="w-4 h-4 text-[#0b905d]" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
            <span className="font-semibold text-[#00153f]">{label}:</span>
            <span className={ok ? "text-[#0b905d]" : "text-red-600"}>{ok ? "OK" : err || "Fehler"}</span>
        </div>
    );
}
